import { z } from "zod";

export const stripeBridgeEvent = "froppio:stripe";
const envelope = { channel: z.literal(stripeBridgeEvent), version: z.literal(1) };

export const StripeReturnSchema = z.object({
  businessId: z.uuid(),
  locationId: z.uuid(),
  channel: z.enum(["web", "native"]),
  outcome: z.enum(["returned", "expired", "error"]),
}).strict();
export type StripeReturn = z.infer<typeof StripeReturnSchema>;

export const HostStripeRequestSchema = z.discriminatedUnion("type", [
  z.object({ ...envelope, type: z.literal("ready") }).strict(),
  z.object({
    ...envelope, type: z.literal("open"), id: z.uuid(), userId: z.string().min(1),
    url: z.string().max(2048),
  }).strict(),
]);
export const HostStripeMessageSchema = z.discriminatedUnion("type", [
  z.object({ ...envelope, type: z.literal("resume") }).strict(),
  z.object({ ...envelope, type: z.literal("return"), result: StripeReturnSchema }).strict(),
  z.object({ ...envelope, type: z.literal("opened"), id: z.uuid() }).strict(),
  z.object({ ...envelope, type: z.literal("error"), id: z.uuid() }).strict(),
]);
export type HostStripeMessage = z.infer<typeof HostStripeMessageSchema>;

function cleanUrl(value: string) {
  if (value.includes("\\") || /[\u0000-\u0020]/.test(value)) throw new Error("Invalid URL.");
  const url = new URL(value);
  if (url.username || url.password || url.hash) throw new Error("Invalid URL.");
  return url;
}

function parameterCount(url: URL) {
  let count = 0;
  url.searchParams.forEach(() => { count++; });
  return count;
}

export function stripeLaunchUrl(value: string, apiBase: string, allowLocalHttp = false): string {
  const url = cleanUrl(value);
  const api = cleanUrl(apiBase);
  const local = /^(localhost|127\.0\.0\.1|\[::1\]|10\.0\.2\.2)$/.test(api.hostname) ||
    /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(api.hostname);
  if ((api.protocol !== "https:" && !(allowLocalHttp && local && api.protocol === "http:")) ||
      url.origin !== api.origin || url.pathname !== `${api.pathname.replace(/\/$/, "")}/integrations/stripe/launch` ||
      parameterCount(url) !== 1 || !url.searchParams.has("ticket") ||
      !/^[A-Za-z0-9_-]{20,512}$/.test(url.searchParams.get("ticket") ?? "")) {
    throw new Error("Invalid Stripe launch URL.");
  }
  return url.href;
}

export function parseStripeReturn(value: string, appOrigin: string, requireHttps = true): StripeReturn | null {
  try {
    const url = cleanUrl(value);
    if ((requireHttps && url.protocol !== "https:") || url.origin !== appOrigin || url.pathname !== "/stripe/return") return null;
    const names = ["business_id", "location_id", "channel", "outcome"];
    if (parameterCount(url) !== names.length || names.some(name => url.searchParams.getAll(name).length !== 1)) return null;
    const parsed = StripeReturnSchema.safeParse({
      businessId: url.searchParams.get("business_id"), locationId: url.searchParams.get("location_id"),
      channel: url.searchParams.get("channel"), outcome: url.searchParams.get("outcome"),
    });
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
